import * as Types from '../types';
import { Environment } from '../../../config';
import request from '../../../utils/request';
import { LanguagesEnum } from 'constants/languages';
import _, { StringNullableChain } from 'lodash';
import { varToStringParams } from 'utils/path';

export enum taskType {
  task = 0,
  appointment = 1,
  phoneCall = 2,
}

interface IAddress {
  address1?: string;
  location?: {
    lat: number;
    lng: number;
  };
  deletePrevious?: boolean;
}
interface IDocument {
  mimetype: string;
  seofilename: string;
  altattribute: string;
  titleattribute: string;
  virtualpath: string;
  originalfilename: string;
  filesize: number;
  visible: boolean;
  filetype: number;
  filecontenttype: number;
}

export interface ITask {
  title: string;
  description: string;
  type: taskType.task | taskType.appointment | taskType.phoneCall;
  datestart: string | null;
  dateend: string | null;
  keepprivate: boolean;
  tags: string[];
  color: string[];
  products: (number | undefined)[];
  customers: number[];
  assignees: number[];
  documents: IDocument[];
  address?: IAddress | null;
  status?: number;
  extra?: string;
  pictures?: any;
  onFinish?: () => void | null;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  isLogin?: boolean;
}

export const addNewTask = async (
  payload: ITask
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/task`,
    {
      body: payload,
    }
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};
export interface ITaskDetail extends ITask {
  id: number;
}
export const updateTask = async (
  payload: ITaskDetail
): Promise<Types.ResponseOutput> => {
  const body = {
    title: payload.title,
    description: payload.description,
    type: payload.type,
    datestart: payload.datestart,
    dateend: payload.dateend,
    keepprivate: payload.keepprivate,
    tags: payload.tags,
    color: payload.color,
    address: payload.address,
    status: payload.status,
    documents: {
      items: payload.documents,
      deletePrevious: payload.documents.length > 0 ? true : false,
    },
    products: {
      items: payload.products,
      deletePrevious: true,
    },
    customers: {
      items: payload.customers,
      deletePrevious: true,
    },
    assignees: {
      items: payload.assignees,
      deletePrevious: true,
    },
  };
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/task/${payload.id}`,
    {
      body: body,
    }
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

interface IPaging {
  sortField: string;
  page?: number;
  pageSize?: number;
  order: 'asc' | 'desc';
}

export interface ITaskInput extends IPaging {
  name: string | null;
  dateStart: string | null;
  dateEnd: string | null;
  type: number | null;
  agents: string;
  sharingGuid?: string | number | null;
  id?: number;
}
export interface ICommentListInput extends IPaging {
  taskId: number;
}

export const fetchTaskList = async (
  payload: ITaskInput
): Promise<Types.ResponseOutput> => {
  let query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
      { key: 'name', value: payload?.name?.toString() },
      { key: 'agents', value: payload?.agents?.toString() },
      { key: 'type', value: payload?.type || '' },
      { key: 'sharingGuid', value: payload?.sharingGuid?.toString() },
    ],
  });

  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/task/list${query}`
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchProductAppointmentAndTaskList = async (
  payload: ITaskInput
): Promise<Types.ResponseOutput> => {
  console.log(payload);
  let query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
      { key: 'name', value: payload?.name?.toString() },
      { key: 'agents', value: payload?.agents?.toString() },
      { key: 'type', value: payload?.type || '' },
      { key: 'id', value: payload?.id?.toString() },
    ],
  });

  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/task/product/list${query}`
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchTaskListByCustomer = async (
  payload: ITaskInput
): Promise<Types.ResponseOutput> => {
  const nameQuery = payload.name ? `&name=${payload.name}` : '';
  const agentsQuery = payload.agents !== '' ? `&agents=${payload.agents}` : '';
  const typeQuery = payload.type ? `&type=${payload.type}` : '';

  const dateStartQuery = payload.dateStart
    ? `&dateStart=${payload.dateStart}`
    : '';
  const dateEndQuery = payload.dateEnd ? `&dateEnd=${payload.dateEnd}` : '';

  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/task/list-by-customer?page=${
      payload.page
    }&pageSize=${payload.pageSize}&sortField=${
      payload.sortField
    }&order=${payload.order.toUpperCase()}${nameQuery}${dateStartQuery}${dateEndQuery}${typeQuery}${agentsQuery}`
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchTaskDetail = async (payload: {
  id: number;
}): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/task/${payload.id}`
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};
export const deleteTask = async (payload: {
  id: number;
}): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'delete',
    `${Environment.API_BASE_URL}/api/task/${payload.id}`
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchCustomerNames = async (): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/task/customers/mini-profile`
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};
export const fetchAgentNames = async (): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/task/agents/mini-profile`
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchAssignees = async (): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/task/assignees/mini-profile`
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const changeStatusTask = async (payload: {
  id: number;
  status: number;
}): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/task/${payload.id}/status`,
    {
      body: {
        status: payload.status,
      },
    }
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

interface IComment {
  comment: string;
  documents: IDocument[];
}

export interface ICommentPayload extends IComment {
  id: number;
}
export const commentTask = async (
  payload: ICommentPayload
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/task/${payload.id}/comment`,
    {
      body: {
        comment: payload.comment,
        documents: payload.documents,
      },
    }
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const fetchCommentTask = async (
  payload: ICommentListInput
): Promise<Types.ResponseOutput> => {
  let query = varToStringParams({
    variablesArray: [
      { key: 'page', value: payload.page?.toString() },
      { key: 'pageSize', value: payload.pageSize?.toString() },
      { key: 'sortField', value: payload.sortField },
      { key: 'order', value: payload?.order?.toUpperCase() },
    ],
  });
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/task/${payload.taskId}/comment${query}`
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const likeTask = async (payload: {
  id: number;
}): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/task/${payload.id}/like`
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

export const reorderTask = async (payload: {
  tasks: ITask[];
}): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'post',
    `${Environment.API_BASE_URL}/api/task/reorder`,
    {
      body: {
        tasks: payload.tasks,
      },
    }
  );

  let _data: any = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};
